import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { get } from 'lodash';

import { getUrlWithStickyParams } from '../../../util';
import { media } from '../../../styles/style-utils';
import { legacyType } from '../../../styles/typography';

import MainCtaButton from '../button/MainCtaButton';
import { withRouteData } from 'react-static';
import { getArtworkUrl, getArtworkDimensions } from '../utils/Contentful';
import ArticleAsset from '../article/ArticleAsset';

const Wrapper = styled.div`
  padding-top: ${props => props.paddingTop || 0};
  padding-bottom: ${props => props.paddingBottom || 0};
  padding-left: ${props => props.paddingLeft || 0};
  padding-right: ${props => props.paddingRight || 0};
  background-color: ${props => props.backgroundColor || 'inherit'};
  margin-bottom: ${props => props.marginBottom};
  text-align: ${props => props.mobileAlignment || 'left'};
  align-items: ${props => (props.mobileAlignment === 'left' ? 'flex-start' : 'center')};
  word-break: break-word;
  margin-left: auto;
  margin-right: auto;
  border-radius: ${props => props.borderRadius || 0};
  display: flex;
  flex-direction: column;
  height: 100%;

  h2 {
    font-size: ${props => props.headlineFontSize || 'var(--fs-xl)'};
    color: ${props => props.headlineColor || 'inherit'};
  }

  ${media.mediumLarge`
    text-align: ${props => props.desktopAlignment || 'center'};
    align-items: ${props => (props.desktopAlignment === 'left' ? 'flex-start' : 'center')};
    max-width: ${props => `${props.maxWidth || 100}%`};
  `}

  blockquote:before {
    margin-left: ${props => (props.mobileAlignment === 'center' ? 'auto' : '0')};
    margin-right: ${props => (props.mobileAlignment === 'center' ? 'auto' : '0')};
    display: block;
    margin-bottom: 1rem;
    margin-top: 3rem;
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 44.6 33.1'%3E%3Cpath fill='white' d='M14.1,13.4l5.8-11.8C20.2,0.8,19.7,0,18.9,0H15c-0.3,0-0.7,0.1-0.8,0.4L1.8,17.1c-1.3,1.9-2,4.3-1.7,6.8c0.5,5,4.7,8.9,9.7,9.1c5.9,0.3,10.8-4.4,10.8-10.3C20.5,18.6,17.8,14.9,14.1,13.4z'/%3E%3Cpath fill='white' d='M38.1,13.4l5.8-11.8C44.2,0.8,43.7,0,42.9,0H39c-0.3,0-0.7,0.1-0.8,0.4L25.8,17.1c-1.3,1.9-2,4.3-1.7,6.8c0.5,5,4.7,8.9,9.7,9.1c5.9,0.3,10.8-4.4,10.8-10.3C44.5,18.6,41.9,14.9,38.1,13.4z'/%3E%3C/svg%3E");
    width: 3rem;

    ${media.mediumLarge`
      margin-left: ${props => (props.desktopAlignment === 'left' ? '0' : 'auto')};
      margin-right: ${props => (props.desktopAlignment === 'left' ? '0' : 'auto')};
    `}
  }

  blockquote {
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;

    p {
      margin-bottom: 16px;
    }
  }

  blockquote:not(:first-child) {
    margin-block-start: 4rem;
  }
`;

const Body = styled.div`
  ${legacyType('sm', 'body')};
  text-align: ${props => (props.justifyAlignBody ? 'justify' : 'inherit')};
  color: ${props => props.bodyColor || 'inherit'};
  width: 100%;

  a {
    text-decoration: underline;
  }

  p {
    font-size: ${props => props.fontSize || 'var(--fs-sm)'};
  }
`;

const OrderedList = styled.ol`
  list-style-type: auto;
  padding-left: revert;
  margin-top: revert;
  margin-bottom: revert;
`;

const UnorderedList = styled.ul`
  list-style-type: disc;
  padding-left: revert;
  margin-top: revert;
  margin-bottom: revert;

  > li ul {
    list-style-type: circle;
  }
`;

const TableScroller = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: var(--fr);
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  text-align: left;

  th,
  td {
    border: 0.5px solid var(--c-slate);
    padding: calc(var(--fr) * 0.5) var(--fr);
    vertical-align: top;
  }

  th {
    font-weight: 900;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const IconTop = styled.img`
  width: ${props => props.iconWidth || 'calc(var(--fr) * 10)'};
  margin-bottom: ${props => props.marginBottom};
`;

const IconBottom = styled.img`
  width: ${props => props.iconWidth || 'calc(var(--fr) * 10)'};
  margin-top: auto;
`;

const getCellSpanProps = node => {
  const colspan = get(node, 'data.colspan');
  const rowspan = get(node, 'data.rowspan');

  return {
    ...(colspan > 1 && { colSpan: colspan }),
    ...(rowspan > 1 && { rowSpan: rowspan }),
  };
};

export const getOptions = basename => {
  return {
    renderText: text => {
      return text.split('\n').reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
    renderNode: {
      [BLOCKS.UL_LIST]: (node, children) => <UnorderedList>{children}</UnorderedList>,
      [BLOCKS.OL_LIST]: (node, children) => <OrderedList>{children}</OrderedList>,
      ['table']: (node, children) => (
        <TableScroller>
          <Table>
            <tbody>{children}</tbody>
          </Table>
        </TableScroller>
      ),
      ['table-row']: (node, children) => <tr>{children}</tr>,
      ['table-cell']: (node, children) => <td {...getCellSpanProps(node)}>{children}</td>,
      ['table-header-cell']: (node, children) => <th {...getCellSpanProps(node)}>{children}</th>,
      [INLINES.HYPERLINK]: (node, children) => {
        const urlWithStickyParams = getUrlWithStickyParams(node.data.uri, basename);

        return <a href={urlWithStickyParams}>{children}</a>;
      },
      [BLOCKS.EMBEDDED_ASSET]: node => {
        if (!get(node, 'data.target.fields')) {
          return null;
        }

        const { description, file } = node.data.target.fields;

        return (
          <ArticleAsset
            description={description}
            url={get(file, 'url')}
            contentType={file.contentType}
          />
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const { target } = node.data;
        switch (target.sys.contentType.sys.id) {
          case 'landingPagesCtaButton':
            return <MainCtaButton content={target.fields} />;
          default:
            return null;
        }
      },
    },
  };
};

class TextBlockWithIcon extends React.Component {
  constructor() {
    super();
    this.state = {
      body: null,
    };
  }

  componentDidMount() {
    const body = documentToReactComponents(
      this.props.content.body,
      getOptions(this.props.basename)
    );
    // eslint-disable-next-line
    this.setState({ body });
  }

  getMarginTop() {
    const { headline, icon } = this.props.content;
    const { body } = this.state;
    if (body) {
      return '0';
    }
    if (headline || icon) {
      return '2rem';
    }
    return '0';
  }

  render() {
    const { content, marginBottom, className, srcBlockName } = this.props;
    const {
      icon,
      iconWidth,
      headline,
      ctaButton,
      justifyAlignBody,
      maxWidth,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      mobileAlignment,
      desktopAlignment,
      headlineFontSize,
      headlineColor,
      bodyColor,
      bodyFontSize,
      iconPosition,
      backgroundColor,
      borderRadius,
    } = content;
    const { body } = this.state;

    return (
      <Wrapper
        maxWidth={maxWidth}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
        paddingLeft={paddingLeft}
        paddingRight={paddingRight}
        mobileAlignment={mobileAlignment}
        desktopAlignment={desktopAlignment}
        marginBottom={marginBottom}
        headlineFontSize={headlineFontSize}
        headlineColor={headlineColor}
        className={className}
        backgroundColor={backgroundColor}
        borderRadius={borderRadius}
      >
        {icon && iconPosition !== 'bottom' && (
          <IconTop
            src={getArtworkUrl(icon)}
            {...getArtworkDimensions(icon)}
            alt=""
            marginBottom={headline || body ? '2rem' : '0'}
            iconWidth={iconWidth}
          />
        )}
        {headline && (
          <h2>
            {headline.split('\\n').reduce((acc, segment, i) => {
              return [...acc, i > 0 && <br key={i} />, segment];
            }, [])}
          </h2>
        )}
        {body && (
          <Body justifyAlignBody={justifyAlignBody} bodyColor={bodyColor} fontSize={bodyFontSize}>
            {body}
          </Body>
        )}
        {get(ctaButton, 'fields') && (
          <MainCtaButton
            content={ctaButton.fields}
            marginTop={this.getMarginTop()}
            trackingData={{
              title: get(ctaButton, 'fields.title'),
              from: srcBlockName,
            }}
          />
        )}
        {icon && iconPosition === 'bottom' && (
          <IconBottom
            src={getArtworkUrl(icon)}
            {...getArtworkDimensions(icon)}
            alt=""
            marginBottom={headline || body ? '2rem' : '0'}
            iconWidth={iconWidth}
          />
        )}
      </Wrapper>
    );
  }
}

TextBlockWithIcon.propTypes = {
  content: PropTypes.object,
  marginBottom: PropTypes.string,
  className: PropTypes.string,
  srcBlockName: PropTypes.string.isRequired,
  basename: PropTypes.string.isRequired,
};

export default withRouteData(TextBlockWithIcon);



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/components/main/textBlock/TextBlockWithIcon.js